import { useState, useRef } from 'react';
import { login } from '../services/authService';
import { validateApiKey } from '../services/apiKeyService';
import { Turnstile } from '@marsidev/react-turnstile';
import './login.css';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACvepbnS8KXoJfUc';

const DISABLE_RECAPTCHA = (window.env?.DISABLE_RECAPTCHA || 'false') === 'true';

export const Login = ({ onLoginSuccess }) => {
  const recaptchaRef = useRef(null);

  const [loginMode, setLoginMode] = useState('apikey'); // 'credentials' ou 'apikey'
  const [formData, setFormData] = useState({
    username: '',
    senha: '',
    apiKey: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: false,
      }));
    }
    
    if (error) {
      setError('');
    }
  };

  const validateForm = () => {
    const errors = {};

    if (loginMode === 'credentials') {
      if (!formData.username.trim()) {
        errors.username = true;
      }

      if (!formData.senha.trim()) {
        errors.senha = true;
      }
    } else if (loginMode === 'apikey') {
      if (!formData.apiKey.trim()) {
        errors.apiKey = true;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (loginMode === 'apikey') {
        const result = await validateApiKey(formData.apiKey);

        if (result.success) {
          if (onLoginSuccess) {
            onLoginSuccess(result.apiKey, 'apikey');
          }
        } else {
          setError(result.error || 'ApiKey inválida');
          setFieldErrors({
            apiKey: true,
          });
        }
      } else {
        let recaptchaToken = null;

        if (!DISABLE_RECAPTCHA) {
          if (!turnstileToken) {
            setError('Por favor, complete o desafio de segurança');
            setLoading(false);
            return;
          }
        }

        const result = await login(
          null,
          formData.username,
          formData.senha,
          turnstileToken
        );

        if (result.success) {
          if (onLoginSuccess) {
            onLoginSuccess(result.token, 'credentials');
          }
        } else {
          setError(result.error || 'Erro ao fazer login. Verifique suas credenciais.');
          setFieldErrors({
            username: true,
            senha: true,
          });
          if (!DISABLE_RECAPTCHA) {
            recaptchaRef.current?.reset();
          }
        }
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente mais tarde.');
      if (!DISABLE_RECAPTCHA && loginMode === 'credentials') {
        recaptchaRef.current?.reset();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <img 
            src="/img/LOGO_PACTO_AZUIL_BRANCO_RGB.png" 
            alt="Logo PACTO" 
            className="login-logo"
            style={{ filter: 'brightness(0) saturate(100%) invert(13%) sepia(35%) saturate(1847%) hue-rotate(195deg) brightness(95%) contrast(93%)' }}
          />
          <h1 className="login-title">Documentação da API</h1>
          <p className="login-subtitle">Faça login para acessar a documentação</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Seletor de modo de login */}
          <div className="login-mode-selector" style={{ marginBottom: '20px' }}>
            <button
              type="button"
              className={`mode-button ${loginMode === 'apikey' ? 'active' : ''}`}
              onClick={() => {
                setLoginMode('apikey');
                setError('');
                setFieldErrors({});
              }}
              disabled={loading}
              style={{
                flex: 1,
                padding: '10px',
                border: loginMode === 'apikey' ? '2px solid #1a4d8f' : '1px solid #ddd',
                backgroundColor: loginMode === 'apikey' ? '#1a4d8f' : '#fff',
                color: loginMode === 'apikey' ? '#fff' : '#333',
                borderRadius: '4px 0 0 4px',
                cursor: 'pointer',
                fontWeight: loginMode === 'apikey' ? 'bold' : 'normal',
                transition: 'all 0.3s ease'
              }}
            >
              ApiKey
            </button>
            <button
              type="button"
              className={`mode-button ${loginMode === 'credentials' ? 'active' : ''}`}
              onClick={() => {
                setLoginMode('credentials');
                setError('');
                setFieldErrors({});
              }}
              disabled={loading}
              style={{
                flex: 1,
                padding: '10px',
                border: loginMode === 'credentials' ? '2px solid #1a4d8f' : '1px solid #ddd',
                backgroundColor: loginMode === 'credentials' ? '#1a4d8f' : '#fff',
                color: loginMode === 'credentials' ? '#fff' : '#333',
                borderRadius: '0 4px 4px 0',
                cursor: 'pointer',
                fontWeight: loginMode === 'credentials' ? 'bold' : 'normal',
                transition: 'all 0.3s ease'
              }}
            >
              Usuário/Senha
            </button>
          </div>

          {loginMode === 'credentials' ? (
            <>
              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  E-mail ou Telefone
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.username ? 'error' : ''}`}
                  placeholder="Digite seu e-mail ou telefone"
                  disabled={loading}
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="senha" className="form-label">
                  Senha
                </label>
                <input
                  type="password"
                  id="senha"
                  name="senha"
                  value={formData.senha}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.senha ? 'error' : ''}`}
                  placeholder="Digite sua senha"
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label htmlFor="apiKey" className="form-label">
                ApiKey
              </label>
              <input
                type="text"
                id="apiKey"
                name="apiKey"
                value={formData.apiKey}
                onChange={handleChange}
                className={`form-input ${fieldErrors.apiKey ? 'error' : ''}`}
                placeholder="Cole sua ApiKey aqui"
                disabled={loading}
                autoComplete="off"
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
              <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                💡 A ApiKey será usada para autenticar suas requisições na documentação
              </p>
            </div>
          )}

          {!DISABLE_RECAPTCHA && loginMode === 'credentials' && (
            <div className="form-group" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
                options={{
                  theme: 'light',
                }}
              />
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner-login"></span>
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </button>
        </form>

        <div className="login-footer">
          {!DISABLE_RECAPTCHA && (
            <p style={{ fontSize: '11px', color: '#999', marginBottom: '10px' }}>
              Protegido por reCAPTCHA v2
            </p>
          )}
          <p>© 2024 PACTO Soluções. Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;

